(function(angular, _, moment) {
    /**
     * @ngdoc object
     * @name ComplexInputDatepickerController
     * @description The controller for the complex input of type datepicker. Usually it handles dimensions that are of date format and have
     * a `timeFormat` property
     */
    class ComplexInputDatepickerController {
        static get $inject() {
            return [
                'Monads',
                '$timeout',
                'COMPLEX_INPUT_UI_DATE_FORMATS',
                'COMPLEX_INPUT_API_DATE_FORMATS'
            ];
        }

        constructor(Monads, $timeout, COMPLEX_INPUT_UI_DATE_FORMATS, COMPLEX_INPUT_API_DATE_FORMATS) {
            this.Monads = Monads;
            this.$timeout = $timeout;
            this.uiDateFormats = COMPLEX_INPUT_UI_DATE_FORMATS;
            this.apiDateFormats = COMPLEX_INPUT_API_DATE_FORMATS;

            /**
             * The current mode for the datepicker
             * Valid values day|month|year
             * @type {null}
             */
            this.mode = null;
            this.minMode = null;
            this.maxMode = null;

            /**
             * The options for the datepicker
             * @type {null}
             */
            this.datepickerOptions = null;

            /**
             * Defines the minimum available date
             * @type {null}
             */
            this.minDate = null;

            /**
             * Defines the maximum available date
             * @type {number}
             */
            this.maxDate = Date.now();

            /**
             * The Date instance constructed via the binded input and manipulated by the datepicker
             * @type {null}
             */
            this.dateValue = null;
        }

        $onInit() {
            this.layer = _.includes(this.Monads.LAYERS, this.layer) ? this.layer : this.Monads.RAW_DATA;
        }

        $onChanges(changeList) {
            this.refreshDatepicker();

            if (_.has(changeList, 'arrangeType.currentValue')) {
                return this.onArrangeTypeChange(changeList.arrangeType.currentValue);
            }

            if (_.has(changeList, 'value.currentValue')) {
                return this.onValueChange(changeList.value.currentValue);
            }

            return this;
        }

        /**
         * @ngdoc method
         * @name ComplexInputDatepickerController#onArrangeTypeChange
         * @description Callback invoked once the binding arrange type changed (Re-initializes the datepicker)
         * @param {string} arrangeType The new arrange type value
         * @returns {ComplexInputDatepickerController}
         */
        onArrangeTypeChange(arrangeType) {
            const monad = this.Monads.layer(this.layer).monad(arrangeType);

            if (!monad) {
                return this;
            }

            if (_.get(monad, 'timeFormat', '')) {
                this.mode = _.get(monad, 'timeFormat');
                this.minMode = this.mode;
                this.maxMode = this.mode;

                this.datepickerOptions = this.getDatePickerOptions();

                this.onValueChange(this.value);
            }

            return this;
        }

        /**
         * @ngdoc method
         * @name ComplexInputDatepickerController#onValueChange
         * @description Callback on binding value change
         * @param {string} value
         * @returns {ComplexInputDatepickerController}
         */
        onValueChange(value) {
            if (!value) {
                this.value = '';
                this.dateValue = null;
                return this;
            }

            if (this.apiDateFormats[this.mode]) {
                this.dateValue = moment(
                    value, this.apiDateFormats[this.mode]
                ).toDate();
            }

            return this;
        }

        /**
         * @ngdoc method
         * @name ComplexInputDatepickerController#submitValue
         * @description Callback once a date was selected in the datepicker. Notifies the parent component on the new value
         * @returns {ComplexInputDatepickerController}
         */
        submitValue() {
            const dateFormat = this.apiDateFormats[this.mode];

            if (!dateFormat) {
                return this;
            }

            const date = moment(this.dateValue);

            this.value = date.isValid() ? date.format(dateFormat) : '';

            this.onChange({$value: this.value});

            return this.closeDatepicker();
        }

        /**
         * @ngdoc method
         * @name ComplexInputDatepickerController#closeDatepicker
         * @description Closes the datepicker
         * @returns {ComplexInputDatepickerController}
         */
        closeDatepicker() {
            _.set(this.datepickerOptions, 'isOpen', false);

            return this;
        }

        /**
         * @ngdoc method
         * @name ComplexInputDatepickerController#openDatepicker
         * @description Opens the datepicker
         * @returns {ComplexInputDatepickerController}
         */
        openDatepicker() {
            _.set(this.datepickerOptions, 'isOpen', true);

            return this;
        }

        /**
         * @ngdoc method
         * @name ComplexInputDatepickerController#refreshDatepicker
         * @description Will quickly hide and show the 3rd party date picker template allowing for better rendering
         * @returns {ComplexInputDatepickerController}
         */
        refreshDatepicker() {
            if (_.get(this.datepickerOptions, 'isOpen')) {
                this.closeDatepicker();
                this.$timeout(() => this.openDatepicker());
            }

            return this;
        }

        /**
         * @ngdoc method
         * @name ComplexInputDatepickerController#getDatePickerOptions
         * @description Calculates the datepicker options|configuration based on the monad|language definitions
         * @returns {Object}
         */
        getDatePickerOptions() {
            return {
                datepickerOptions: {
                    startingDay: 1,
                    showWeeks: false,
                    datepickerMode: `'${this.mode}'`,
                    minMode: this.minMode,
                    maxMode: this.maxMode,
                    // minDate: this.minDate,
                    // maxDate: this.maxDate,
                    formatDayTitle: _.get(this.uiDateFormats, `${this.language}.month.boot`),
                    formatMonthTitle: _.get(this.uiDateFormats, `${this.language}.year.boot`)
                },
                isOpen: false,
                showButtonBar: false,
                format: _.get(this.uiDateFormats, `${this.language}.${this.mode}.boot`) // boot = bootstrap
            };
        }
    }

    angular.module('wtApp.arrangeTypeInput')
        .component('complexInputDatepicker', {
            bindings: {
                /**
                 * The arrange type name
                 */
                arrangeType: '<',

                /**
                 * The arrange type layer. See wt-query-builder Monads.LAYERS
                 * @type {string}
                 * @default 'RAW_DATA'
                 */
                layer: '<?',

                /**
                 * The users language code
                 * @type {string}
                 * @default 'en'
                 */
                language: '<?',

                /**
                 * The current value to init the datepicker
                 * @type {string|undefined}
                 * @default undefined
                 */
                value: '<?',

                /**
                 * Is the input read only
                 * @type {boolean}
                 * @default false
                 */
                readOnly: '<?',

                /**
                 * Binding to trigger rendering
                 * @type {string}
                 */
                renderTrigger: '<?',

                /**
                 * Expression to evaluate when the value has changed. New value is available via $value variable.
                 * @type {Function}
                 * @default angular.noop
                 */
                onChange: '&?'
            },
            controller: ComplexInputDatepickerController,
            controllerAs: '$ctrl',
            template: ['$templateCache', function($templateCache) {
                return $templateCache.get('src/components/complex-input-datepicker/complex-input-datepicker.component.html');
            }]
        });
})(window.angular, window._, window.moment);
